import { useState, useEffect } from 'react';
import CheckboxList from './CheckboxList.js';
import Dropdown from './Dropdown.js';
import { getFriendlyResultsCount } from './utils/results-helpers';
import { GoogleAutocomplete } from './GoogleAutocomplete.js';
import { GoogleMapsAutocomplete } from './GoogleMapsAutocomplete';
import RadioButtonList from './RadioButtonList.js';
import Toggle from './Toggle.js';

import { APIProvider } from '@vis.gl/react-google-maps';

export default function SearchForm({ config, onChange, formId, ctaContent, isMobile, isMap, mapResults, heading }) {

    const areas = config.SearchRadii?.map(r => ({ name: r.Value, value: r.Key }));
    const propertyTypes = config.PropertyTypes != null && config.PropertyTypes.map(r => ({ name: r.Value, value: r.Key }));
    const ncas = config.NationalCharacterAreas != null && config.NationalCharacterAreas.map(r => ({ name: r.Key, value: r.Value }));
    const lpas = config.LocalPlanningAuthorities != null && config.LocalPlanningAuthorities.map(r => ({ name: r.Key, value: r.Value }));
    const unitTypes = config.UnitTypes != null && config.UnitTypes.map(r => ({ name: r, value: r }));
    const projectTypes = config.ProjectTypes != null && config.ProjectTypes.map(r => ({ name: r, value: r }));
    const vintageYears = config.VerifiedBeforeYears != null && config.VerifiedBeforeYears.map(r => ({ name: r, value: r }));
    const sizesFrom = config.ShowSizeFilter && config.SizeFrom.map(r => ({ name: r.Value, value: r.Key }));
    const sizesTo = config.ShowSizeFilter && config.SizeTo.map(r => ({ name: r.Value, value: r.Key }));
    const pricesFrom = config.ShowPriceFilter && config.PricesFrom.map(r => ({ name: r.Value, value: r.Key }));
    const pricesTo = config.ShowPriceFilter && config.PricesTo.map(r => ({ name: r.Value, value: r.Key }));
    const rentalPricesFrom = config.ShowPriceFilter && config.RentalPricesFrom.map(r => ({ name: r.Value, value: r.Key }));
    const rentalPricesTo = config.ShowPriceFilter && config.RentalPricesTo.map(r => ({ name: r.Value, value: r.Key }));
    const [placeBounds, setPlaceBounds] = useState(config.Bounds);
    const [placeLatLng, setPlaceLatLng] = useState(config.LatLng);
    const [searchType, setSearchType] = useState(config.LocationOrProperty ?? 'location'); // This is really badly named!
    const [propertyName, setPropertyName] = useState(config.PropertyName);

    const [isSale, setIsSale] = useState(config.SearchType == 0);
    const [showFilters, toggleFilters] = useState(false);
    const [ptLabel, setPtLabel] = useState(buildPropertyTypesLabel(config.PropertyType));
    const [sizeLabel, setSizeLabel] = useState(buildSizeLabel(config.MinSize, config.MaxSize));
    const [priceLabel, setPriceLabel] = useState(buildPriceLabel(config.MinPrice, config.MaxPrice));
    const [rentalPriceLabel, setRentalPriceLabel] = useState(buildRentalPriceLabel(config.MinRentalPrice, config.MaxRentalPrice));
    const [ncaLabel, setNcaLabel] = useState(buildNcaLabel(config.NationalCharacterArea));
    const [lpaLabel, setLpaLabel] = useState(buildLpaLabel(config.LocalPlanningAuthority));
    const [unitTypeLabel, setUnitTypeLabel] = useState(buildBasicLabel(config.UnitType, "All unit types"));
    const [projectTypeLabel, setProjectTypeLabel] = useState(buildBasicLabel(config.ProjectType, "All project types"));
    const [vintageYearLabel, setVintageYearLabel] = useState(config.VerifiedBeforeYear == null || config.VerifiedBeforeYear == -1 ? "All vintages" : config.VerifiedBeforeYear);

    let show = true;
    const filterCount = countActiveFilters();
    const radiusLabel = getRadiusLabel();
    const filterButtonWidth = isMap ? 3 : (12 / (1 + config.ShowRadiusFilter + config.ShowPropertyTypeList + config.ShowSizeFilter + config.ShowPriceFilter +
        (config.PropertySearchType == 'bng' ? 2 : 0) +
        (config.PropertySearchType == 'carboncredits' ? 3 : 0)
    ));

    let shouldShowFilterButton = checkShouldShowFilterButton();

    useEffect(() => {
        onChange();
    }, [placeBounds, placeLatLng])


    function setPlace(place, label) {
        if (place != null && place.viewport != null) {
            setPlaceBounds(place.viewport.getSouthWest().toString().replace(/[()]/g, '') + ',' + place.viewport.getNorthEast().toString().replace(/[()]/g, ''));
            setPlaceLatLng(place.location.toString().replace(/[()]/g, ''));
        }
        else if (place != null && place.geocode != null) {
            setPlaceBounds(null);
            setPlaceLatLng(place.geocode.latitude + ',' + place.geocode.longitude)
        } else {
            setPlaceBounds(null);
            setPlaceLatLng(null);
        }
        const lm = document.getElementById('lm');
        if (label != null && searchType == 'location' && lm != null) {
            lm.value = label;
        }
    }

    function buildPropertyTypesLabel(pt) {
        if (pt == null || pt.length == 0) {
            return 'All property types';
        }

        if (pt.constructor === Array) {
            return pt.map(x => x.replace('_', '')).join(', ');
        }
        return pt.replace('_', '');
    }

    function buildNcaLabel(nca) {
        if (nca == null || nca.length == 0) {
            return 'All national character areas';
        }

        if (nca.constructor === Array && config.NationalCharacterAreas.length) {
            let resolved = nca.map(x => { return config.NationalCharacterAreas.filter(f => f.Value == x)[0]?.Key; });
            return resolved.join(', ');
        }
        return nca;
    }
    function buildLpaLabel(lpa) {
        if (lpa == null || lpa.length == 0) {
            return 'All local planning authorities';
        }

        if (lpa.constructor === Array && config.LocalPlanningAuthorities.length) {
            let resolved = lpa.map(x => { return config.LocalPlanningAuthorities.filter(f => f.Value == x)[0].Key; });

            return resolved.join(', ');
        }
        return lpa;
    }

    function buildBasicLabel(formValue, defaultText) {
        if (formValue == null || formValue.length == 0) {
            return defaultText;
        }

        if (formValue.constructor === Array) {
            return formValue.join(', ');
        }
        return formValue;
    }

    function buildFilterLabel(formElementName, defaultText) {
        const form = document.getElementById(formId);
        let vs = form[formElementName];

        let label = [];
        for (let i = 0; i < vs.length; i++) {
            if (vs[i].checked) {
                label[label.length] = vs[i].getAttribute('data-label');
            }
        }
        return label.length == 0 ? defaultText : label.join(', ')
    }

    function buildSizeLabel(minSize, maxSize) {
        const sizeUnit = config.SizeUnit;

        const result = (minSize == null || minSize == -1) && (maxSize == null || maxSize == -1) ? { Label: 'Any size (' + sizeUnit + ')', Applied: false }
            : minSize != null && minSize >= 0 && maxSize != null && maxSize == 10000000 ? { Label: Number(minSize).toLocaleString() + ' ' + sizeUnit + ' and above', Applied: true }
                : minSize != null && minSize >= 0 && maxSize != null && maxSize > 0 ? { Label: Number(minSize).toLocaleString() + ' - ' + Number(maxSize).toLocaleString() + ' ' + sizeUnit, Applied: true }
                    : minSize != null && minSize >= 0 ? { Label: 'From ' + Number(minSize).toLocaleString() + ' ' + sizeUnit, Applied: true }
                        : maxSize == 10000000 ? { Label: 'Any size', Applied: true } : { Label: 'Up to ' + Number(maxSize).toLocaleString() + ' ' + sizeUnit, Applied: true };

        return result.Label;
    }

    function updatePriceLabel() {
        const form = document.getElementById(formId);
        if (form.MinPrice != null) {
            const minPrice = form.MinPrice.value;
            const maxPrice = form.MaxPrice.value;
            const label = buildPriceLabel(minPrice, maxPrice, true);

            setPriceLabel(label);
        } else {
            setPriceLabel('Any price');
        }
    }

    function buildPriceLabel(minPrice, maxPrice) {

        const result = (minPrice == null || minPrice == -1) && (maxPrice == null || maxPrice == -1) ? { Label: 'Any price', Applied: false }
            : minPrice != null && minPrice > 0 && maxPrice != null && maxPrice == 10000000 ? { Label: '£' + Number(minPrice).toLocaleString() + ' and above', Applied: true }
                : minPrice != null && minPrice > 0 && maxPrice != null && maxPrice > 0 ? { Label: '£' + Number(minPrice).toLocaleString() + ' - ' + '£' + Number(maxPrice).toLocaleString(), Applied: true }
                    : minPrice != null && minPrice > 0 ? { Label: 'From ' + '£' + Number(minPrice).toLocaleString(), Applied: true }
                        : maxPrice == 10000000 ? { Label: 'Any price', Applied: true } : { Label: 'Up to ' + '£' + Number(maxPrice).toLocaleString(), Applied: true };

        return result.Label;
    }

    function updateRentalPriceLabel() {
        const form = document.getElementById(formId);

        if (form.MinRentalPrice != null) {
            const minPrice = form.MinRentalPrice.value;
            const maxPrice = form.MaxRentalPrice.value;
            const label = buildRentalPriceLabel(minPrice, maxPrice, true);

            setRentalPriceLabel(label);
        } else {
            setRentalPriceLabel('Any rent');
        }
    }

    function buildRentalPriceLabel(minPrice, maxPrice) {

        const result = (minPrice == null || minPrice == -1) && (maxPrice == null || maxPrice == -1) ? { Label: 'Any rent', Applied: false }
            : minPrice != null && minPrice > 0 && maxPrice != null && maxPrice == 10000000 ? { Label: '£' + Number(minPrice).toLocaleString() + ' and above', Applied: true }
                : minPrice != null && minPrice > 0 && maxPrice != null && maxPrice > 0 ? { Label: '£' + Number(minPrice).toLocaleString() + ' - ' + '£' + Number(maxPrice).toLocaleString(), Applied: true }
                    : minPrice != null && minPrice > 0 ? { Label: 'From ' + '£' + Number(minPrice).toLocaleString(), Applied: true }
                        : maxPrice == 10000000 ? { Label: 'Any price', Applied: true } : { Label: 'Up to ' + '£' + Number(maxPrice).toLocaleString(), Applied: true };

        return result.Label;
    }

    function updateSizeLabel() {
        const form = document.getElementById(formId);
        const minSize = form.MinSize.value;
        const maxSize = form.MaxSize.value;
        const label = buildSizeLabel(minSize, maxSize, true);

        setSizeLabel(label);
    }

    function updateYearsLabel() {
        const form = document.getElementById(formId);
        const year = form.VerifiedBeforeYear.value;
        if (year == null || year == '-1') {
            setVintageYearLabel('All vintages');
        }
        else {
            setVintageYearLabel(year);
        }
    }

    function countActiveFilters() {
        const form = document.getElementById(formId);
        const c = form != null &&
            (config.ShowRadiusFilter && form.LocationOrProperty?.value == 'location' && form.SearchRadius?.value != -1 ? 1 : 0) +
            (config.ShowPropertyTypeList && form.PropertyType.value != null && Array.from(form.PropertyType).reduce((n, x) => n + (x.checked), 0)) +
            (config.ShowSizeFilter && ((form.MinSize.value != null && form.MinSize.value != -1) || (form.MaxSize.value != null && form.MaxSize.value != -1)) ? 1 : 0) +
            (config.ShowPriceFilter && isSale && ((form.MinPrice?.value != null && form.MinPrice?.value != -1) || (form.MaxPrice?.value != null && form.MaxPrice?.value != -1)) ? 1 : 0) +
            (config.ShowPriceFilter && !isSale && ((form.MinRentalPrice?.value != null && form.MinRentalPrice?.value != -1) || (form.MaxRentalPrice?.value != null && form.MaxRentalPrice?.value != -1)) ? 1 : 0) +
            (form.RemoveSoldStc?.checked ? 1 : 0) +
            (config.ShowTenureFilter && form.Tenure?.value != null && form.Tenure.value != 'Any' ? 1 : 0) +
            (config.PropertySearchType == 'bng' && form.NationalCharacterArea?.value != null && Array.from(form.NationalCharacterArea).reduce((n, x) => n + (x.checked), 0)) +
            (config.PropertySearchType == 'bng' && form.LocalPlanningAuthority?.value != null && Array.from(form.LocalPlanningAuthority).reduce((n, x) => n + (x.checked), 0)) +
            (config.PropertySearchType == 'carboncredits' && form.UnitType?.value != null && Array.from(form.UnitType).reduce((n, x) => n + (x.checked), 0)) +
            (config.PropertySearchType == 'carboncredits' && form.ProjectType?.value != null && Array.from(form.ProjectType).reduce((n, x) => n + (x.checked), 0)) +
            (config.PropertySearchType == 'carboncredits' && form.VerifiedBeforeYear?.value!=null && form.VerifiedBeforeYear.value != '-1' ? 1 : 0)
            ;

        return c;
    }

    function checkShouldShowFilterButton() {
        const form = document.getElementById(formId);
        return form != null && (config.ShowRadiusFilter && form.LocationOrProperty?.value == 'location') ||
            config.ShowPropertyTypeList ||
            config.ShowSizeFilter ||
            config.ShowTenureFilter ||
            config.PropertySearchType == 'bng' || config.PropertySearchType == 'carboncredits';
    }

    function getRadiusLabel() {
        const form = document.getElementById(formId);
        if (config.ShowRadiusFilter && form != null && form.SearchRadius != null && form.SearchRadius.value != -1 && config.SearchRadii != null) {
            const label = config.SearchRadii.filter(x => x.Key == form.SearchRadius.value)[0]?.Value;
            if (label != null) { return label; }
        }
        return "This area only";
    }

    function clearFilters() {
        // Clear all except the primary filters by triggering clicks (not efficient but otherwise state gets all messed up :( )

        const form = document.getElementById(formId);
        if (form.SearchRadius) { form.SearchRadius[0].click(); config.SearchRadius = -1; }
        if (form.PropertyType) {
            clearCheckBoxes(form, 'PropertyType');
        }

        if (form.MinSize) { form.MinSize.value = -1; }
        if (form.MaxSize) { form.MaxSize.value = -1; }
        if (form.RemoveSoldStc) { form.RemoveSoldStc.checked && form.RemoveSoldStc.click(); }
        if (form.Tenure) { form.Tenure[0].click(); }
        if (config.PropertySearchType == 'bng') {
            clearCheckBoxes(form, 'NationalCharacterArea');
            clearCheckBoxes(form, 'LocalPlanningAuthority');
        }
        if (config.PropertySearchType == 'carboncredits') {
            clearCheckBoxes(form, 'UnitType');
            clearCheckBoxes(form, 'ProjectType');
            clearCheckBoxes(form, 'VerifiedBeforeYear');
        }
        onChange();
    }

    function clearCheckBoxes(form, formElementName) {
        for (var i = 0; i < form[formElementName].length; i++) {
            if (form[formElementName][i].checked) { form[formElementName][i].click(); }
        }
    }

    function showFilter(e) {
        e.preventDefault();
        scrollFilters(e.target.getAttribute('href')?.substr(1));
        toggleFilters(true);
    }

    function scrollFilters(id) {
        const body = document.getElementsByClassName('filter-body')[0];
        const target = document.getElementById(id);
        if (target != null) {
            body.scrollTo(0, target.offsetTop - 32);
        }
    }

    function activateSearchBox() {
        if (searchType == 'location') {
            document.getElementById('Location').focus();
        } else {
            document.getElementById('Property').focus();
        }
    }

    function searchClick() {
        if (searchType == 'property') {
            const pm = document.getElementById('pm');
            if (pm != null) pm.value = document.getElementById('Property')?.value;
            onChange();
        } else {
            const lm = document.getElementById('lm');
            if (lm != null) lm.value = document.getElementById('Location')?.value;
            onChange();
        }
        toggleFilters(false);
    }

    return (
        <div className="grid-container fluid bite large before short property-search-page">
            {config.PropertySearchType != 'carboncredits' &&
                <div className="bg-pattern"></div>
            }
            <form id={formId} onSubmit={e => { e.preventDefault(); }} onChange={onChange}>
                <input type="hidden" name="Bounds" defaultValue={placeBounds} />
                <input type="hidden" name="LatLng" defaultValue={placeLatLng} />
                {config.PropertySearchType != 'carboncredits' &&
                    <div className="grid-container search-form">
                        <div className="grid-x">
                            {!isMobile && config.SearchTypes.length > 1 &&
                                <div className="cell medium-3 xlarge-2 mb-2 show-for-medium">
                                    <div className="rounded mr-1 search-type">
                                        <Toggle name="SearchType" label1={config.SearchTypes[0].Value} value1={config.SearchTypes[0].Key} label2={config.SearchTypes[1].Value} value2={config.SearchTypes[1].Key} selected={config.SearchType} onChange={(x) => { setIsSale(x == 0); updatePriceLabel(); updateRentalPriceLabel(); }} />
                                    </div>
                                </div>
                            }
                            {!isMobile && config.SearchTypes.length == 1 &&
                                <input type="hidden" name="SearchType" defaultValue={config.SearchTypes[0].Key} />
                            }
                            {!isMobile &&
                                <div className={'cell mb-2 medium-' + (config.SearchTypes.length > 1 ? '7' : '10') + ' xlarge-' + (config.SearchTypes.length > 1 ? '8' : '10')}>
                                    <div className="rounded flex-container search-bar">
                                        <input type="radio" id="loc" name="LocationOrProperty" value="location" className="show-for-sr" checked={searchType === 'location'} onChange={(e) => { e.stopPropagation(); setSearchType('location') }} />
                                        <input type="radio" id="prop" name="LocationOrProperty" value="property" className="show-for-sr" checked={searchType === 'property'} onChange={(e) => { e.stopPropagation(); setSearchType('property') }} />
                                        <div className={searchType === 'location' ? 'search active' : 'search'}>
                                            <APIProvider apiKey={config.GmKey}>
                                                <div className={'relative' + (show && ' is-active')}>
                                                    <GoogleMapsAutocomplete onPlaceSelect={setPlace} location={config.Location} hasFocus={e => { setSearchType(e); shouldShowFilterButton = shouldShowFilterButton && e == 'location'; isMobile && toggleFilters(true); }} />
                                                </div>
                                            </APIProvider>
                                        </div>
                                        <div className={searchType === 'location' ? 'indicator show-for-medium' : 'indicator show-for-medium right'}>
                                            <svg xmlns="http://www.w3.org/2000/svg" width="32" height="31" viewBox="0 0 32 31" fill="none">
                                                <circle cx="16" cy="15.5" r="15.5" transform="rotate(-90 16 15.5)" fill="#16255B" />
                                                <path className="a-right" d="M13.05 14.5701L8.5 18.8749L13.05 23.1797L13.05 19.7091L24.3543 19.7091L24.3543 18.0407L13.05 18.0407L13.05 14.5701Z" fill="#63BFB5" />
                                                <path className="a-left" d="M19.95 15.6096L24.5 11.3048L19.95 7L19.95 10.4706L8.64565 10.4706L8.64565 12.139L19.95 12.139L19.95 15.6096Z" fill="white" />
                                            </svg>
                                        </div>
                                        <div className={searchType === 'property' ? 'search active relative' : 'search relative'}>
                                            <input name="PropertyName" type="search" className="property-search rounded" placeholder="or keyword" onFocus={e => setSearchType('property')} onChange={(e) => { e.stopPropagation() }} defaultValue={propertyName} />
                                            <svg className="key absolute" xmlns="http://www.w3.org/2000/svg" width="21" height="23" viewBox="0 0 25 23" fill="none">
                                                <path fillRule="evenodd" clipRule="evenodd" d="M20.6425 3.55151L22.0571 2.13687L20.6429 0.722656L9.97656 11.389C8.99573 10.6905 7.79584 10.2797 6.5 10.2797C3.18629 10.2797 0.5 12.966 0.5 16.2797C0.5 19.5934 3.18629 22.2797 6.5 22.2797C9.81371 22.2797 12.5 19.5934 12.5 16.2797C12.5 14.9839 12.0892 13.784 11.3908 12.8032L14.9854 9.20856L17.1063 11.3295L19.2277 9.20814L17.1068 7.08723L18.5212 5.67283L22.0564 9.20809L24.1777 7.08677L20.6425 3.55151ZM2.5 16.2797C2.5 14.0706 4.29086 12.2797 6.5 12.2797C8.70914 12.2797 10.5 14.0706 10.5 16.2797C10.5 18.4889 8.70914 20.2797 6.5 20.2797C4.29086 20.2797 2.5 18.4889 2.5 16.2797Z" fill="black" fillOpacity="0.5" />
                                            </svg>
                                        </div>
                                    </div>
                                </div>
                            }
                            {isMobile &&
                                <div className="cell mb-2">
                                    <div className="rounded flex-container search-bar">
                                        <div className={searchType === 'location' ? 'search active' : 'search'}>
                                            <div className="relative is-active">
                                                <input type="radio" id="loc" name="LocationOrProperty" value="location" className="show-for-sr" checked={searchType === 'location'} onChange={(e) => { e.stopPropagation(); setSearchType('location') }} />
                                                <input type="radio" id="prop" name="LocationOrProperty" value="property" className="show-for-sr" checked={searchType === 'property'} onChange={(e) => { e.stopPropagation(); setSearchType('property') }} />
                                                {searchType == 'location' ?
                                                    <div className="autocomplete-container">
                                                        <input id="lm" className="location-search rounded" placeholder="e.g. location, postcode, street name" onChange={e => e.stopPropagation()} onFocus={() => { setSearchType('location'); toggleFilters(true); activateSearchBox(); }} type="search" autoComplete="off" defaultValue={config.Location} />
                                                        <button type="button" className={navigator.geolocation ? "geo-icon absolute" : "geo-icon hide"}>
                                                            <svg xmlns="http://www.w3.org/2000/svg" width="24" height="25" viewBox="0 0 24 25" fill="none">
                                                                <path fillRule="evenodd" clipRule="evenodd" d="M11 8.5V4.56189C7.38128 5.01314 4.51314 7.88128 4.06189 11.5H8V13.5H4.06189C4.51314 17.1187 7.38128 19.9869 11 20.4381V16.5H13V20.4381C16.6187 19.9869 19.4869 17.1187 19.9381 13.5H16V11.5H19.9381C19.4869 7.88128 16.6187 5.01314 13 4.56189V8.5H11ZM2.04938 13.5C2.51844 18.2244 6.27558 21.9816 11 22.4506V24.5H13V22.4506C17.7244 21.9816 21.4816 18.2244 21.9506 13.5H24V11.5H21.9506C21.4816 6.77558 17.7244 3.01844 13 2.54938V0.5H11V2.54938C6.27558 3.01844 2.51844 6.77558 2.04938 11.5H0V13.5H2.04938Z" fill="black" fillOpacity="0.5" />
                                                            </svg>
                                                            <span className="show-for-sr">Use my location</span>
                                                        </button>
                                                        <a href="#" className="hide-for-medium mobile-filter" title="Show filters" onClick={() => toggleFilters(true)}></a>
                                                    </div>
                                                    :
                                                    <div className={searchType === 'property' ? 'search active relative' : 'search relative'}>
                                                        <input id="pm" type="search" className="property-search rounded" placeholder="e.g. Property name" onChange={(e) => { e.stopPropagation() }} onFocus={() => { setSearchType('property'); toggleFilters(true); activateSearchBox(); }} defaultValue={propertyName} />
                                                        <svg className="key absolute" xmlns="http://www.w3.org/2000/svg" width="21" height="23" viewBox="0 0 25 23" fill="none">
                                                            <path fillRule="evenodd" clipRule="evenodd" d="M20.6425 3.55151L22.0571 2.13687L20.6429 0.722656L9.97656 11.389C8.99573 10.6905 7.79584 10.2797 6.5 10.2797C3.18629 10.2797 0.5 12.966 0.5 16.2797C0.5 19.5934 3.18629 22.2797 6.5 22.2797C9.81371 22.2797 12.5 19.5934 12.5 16.2797C12.5 14.9839 12.0892 13.784 11.3908 12.8032L14.9854 9.20856L17.1063 11.3295L19.2277 9.20814L17.1068 7.08723L18.5212 5.67283L22.0564 9.20809L24.1777 7.08677L20.6425 3.55151ZM2.5 16.2797C2.5 14.0706 4.29086 12.2797 6.5 12.2797C8.70914 12.2797 10.5 14.0706 10.5 16.2797C10.5 18.4889 8.70914 20.2797 6.5 20.2797C4.29086 20.2797 2.5 18.4889 2.5 16.2797Z" fill="black" fillOpacity="0.5" />
                                                        </svg>
                                                        <a href="#" className="hide-for-medium mobile-filter" title="Show filters" onClick={() => toggleFilters(true)}></a>
                                                    </div>
                                                }
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            }
                            <div className="cell mb-2 medium-2 show-for-medium">
                                <button type="submit" className="btn-search rounded ml-1" onClick={onChange}>
                                    <svg className="icon">
                                        <use href="#search"></use>
                                    </svg>
                                    <span className="show-for-sr">Search by location or keyword</span>
                                </button>
                            </div>
                        </div>
                    </div>
                }
                <div className={'grid-container search-form pt-0' + (isMap ? ' fluid results-row' : '')}>
                    {!isMobile &&
                        <div className={'grid-x filter-row ' + config.PropertySearchType}>
                            {!isMap ?
                                <>
                                    {config.ShowRadiusFilter &&
                                        (searchType == 'location' ?
                                        <div className={'cell medium-' + filterButtonWidth}>
                                            <a href="#radiusFilter" onClick={showFilter} className="filter-btn w-100 rounded">
                                                {radiusLabel}
                                            </a>
                                        </div>
                                            :
                                        <div className={'cell medium-' + filterButtonWidth}>
                                            <span className="filter-btn w-100 rounded disabled">
                                                {radiusLabel}
                                            </span>
                                        </div>
                                        )
                                    }
                                    
                                    {config.ShowPropertyTypeList &&
                                        <div className={'cell medium-' + filterButtonWidth}>
                                            <a href="#ptFilter" onClick={showFilter} className="filter-btn w-100 rounded" title={ptLabel}>
                                                {ptLabel}
                                            </a>
                                        </div>
                                    }
                                    {config.PropertySearchType == 'bng' &&
                                        <>
                                            <div className={'cell medium-' + filterButtonWidth}>
                                                <a href="#ncaFilter" onClick={showFilter} className="filter-btn w-100 rounded" title={ncaLabel}>
                                                    {ncaLabel}
                                                </a>
                                            </div>
                                            <div className={'cell medium-' + filterButtonWidth}>
                                                <a href="#lpaFilter" onClick={showFilter} className="filter-btn w-100 rounded" title={lpaLabel}>
                                                    {lpaLabel}
                                                </a>
                                            </div>
                                        </>
                                    }
                                    {config.ShowSizeFilter &&
                                        <div className={'cell medium-' + filterButtonWidth}>
                                            <a href="#sizeFilter" onClick={showFilter} className="filter-btn w-100 rounded">
                                                {sizeLabel}
                                            </a>
                                        </div>
                                    }
                                    {config.ShowPriceFilter && isSale &&
                                        <div className={'cell medium-' + filterButtonWidth}>
                                            <a href="#priceFilter" onClick={showFilter} className="filter-btn w-100 rounded">
                                                {priceLabel}
                                            </a>
                                        </div>
                                    }
                                    {config.ShowPriceFilter && !isSale &&
                                        <div className={'cell medium-' + filterButtonWidth}>
                                            <a href="#rentalPriceFilter" onClick={showFilter} className="filter-btn w-100 rounded">
                                                {rentalPriceLabel}
                                            </a>
                                        </div>
                                    }
                                    {config.PropertySearchType == 'carboncredits' &&
                                        <>
                                            <div className={'cell medium-' + filterButtonWidth}>
                                                <a href="#unitTypeFilter" onClick={showFilter} className="filter-btn w-100 rounded" title={unitTypeLabel}>
                                                    {unitTypeLabel}
                                                </a>
                                            </div>
                                            <div className={'cell medium-' + filterButtonWidth}>
                                                <a href="#projectTypeFilter" onClick={showFilter} className="filter-btn w-100 rounded" title={projectTypeLabel}>
                                                    {projectTypeLabel}
                                                </a>
                                            </div>
                                            <div className={'cell medium-' + filterButtonWidth}>
                                                <a href="#vintageFilter" onClick={showFilter} className="filter-btn w-100 rounded" title={vintageYearLabel}>
                                                    {vintageYearLabel}
                                                </a>
                                            </div>
                                        </>
                                    }
                                </>
                                :
                                <div className="cell medium-9 results-info">
                                    <p className="count-label">{getFriendlyResultsCount(mapResults, config.IncludeBngProperties, 50)}</p>
                                    <h2 className="intro-heading">{heading}</h2>
                                </div>
                            }   
                            
                            {shouldShowFilterButton &&
                                <div className={'cell medium-' + filterButtonWidth}>
                                    <a href="#allFilters" className="filter-btn w-100 rounded filters" onClick={showFilter}>
                                        <svg xmlns="http://www.w3.org/2000/svg" width="14" height="21" viewBox="0 0 14 21" fill="none">
                                            <line x1="11" y1="0.164063" x2="11" y2="20.8314" stroke="white" strokeWidth="2" />
                                            <circle cx="11.0001" cy="15.1642" r="2.00009" fill="#16255B" stroke="white" strokeWidth="2" />
                                            <line x1="3" y1="20.832" x2="3" y2="0.164718" stroke="white" strokeWidth="2" />
                                            <circle cx="2.99991" cy="5.82803" r="2.00009" transform="rotate(-180 2.99991 5.82803)" fill="#16255B" stroke="white" strokeWidth="2" />
                                        </svg>
                                        {'Filters' + (filterCount > 0 ? ' (' + filterCount + ')' : '')}
                                    </a>
                                </div>
                            }
                        </div>
                    }
                    {isMobile && config.PropertySearchType == 'carboncredits' &&
                        <div className='grid-x cta-row carboncredits-filter'>
                            <div className='cell filter-row'>
                                <a href="#allFilters" className="filter-btn w-100 rounded filters" onClick={showFilter}>
                                    <svg xmlns="http://www.w3.org/2000/svg" width="14" height="21" viewBox="0 0 14 21" fill="none">
                                        <line x1="11" y1="0.164063" x2="11" y2="20.8314" stroke="white" strokeWidth="2" />
                                        <circle cx="11.0001" cy="15.1642" r="2.00009" fill="#16255B" stroke="white" strokeWidth="2" />
                                        <line x1="3" y1="20.832" x2="3" y2="0.164718" stroke="white" strokeWidth="2" />
                                        <circle cx="2.99991" cy="5.82803" r="2.00009" transform="rotate(-180 2.99991 5.82803)" fill="#16255B" stroke="white" strokeWidth="2" />
                                    </svg>
                                    {'Filters' + (filterCount > 0 ? ' (' + filterCount + ')' : '')}
                                </a>
                            </div>
                        </div>
                    }
                    {isMobile && isMap &&
                        <div className="grid-x filter-row">
                            <div className="cell results-info">
                                <p className="count-label">{getFriendlyResultsCount(mapResults, config.IncludeBngProperties, 50)}</p>
                                <h2 className="intro-heading">{heading}</h2>
                            </div>
                        </div>
                    }
                    <div className={config.PropertySearchType != 'carboncredits' ? 'grid-x cta-row' : 'show-for-medium pb-5'}>
                        <div className="cell small-12 justify-end">
                            {ctaContent != null && ctaContent.cta && ctaContent.label &&
                                <a href={ctaContent.cta} className="button cta alert">{ctaContent.label}</a>
                            }
                            {!isMap && config.PropertySearchType != 'carboncredits' &&
                                <a href="#" onClick={(e) => { e.preventDefault(); window.location = window.location.href + '&map=true'; }} className="button cta map">Map view</a>
                            }
                        </div>
                    </div>
                </div>

                <div className={'filter-bg' + (showFilters ? ' show' : '') + (isMobile ? ' mob' : '')}>
                    <div className="filter-panel">
                        <div className="filter-header">
                            <a href="#" className="close" onClick={e => { e.preventDefault(); toggleFilters(false); }}>
                                <svg xmlns="http://www.w3.org/2000/svg" width="23" height="24" viewBox="0 0 23 24" fill="none">
                                    <path d="M16.9688 6.34375L5.65504 17.6575" stroke="black" strokeLinecap="round" />
                                    <path d="M16.9688 17.6562L5.65504 6.34254" stroke="black" strokeLinecap="round" />
                                </svg>
                            </a>
                            <h3 className="filter-title">
                                <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none">
                                    <path fillRule="evenodd" clipRule="evenodd" d="M22 16.75C22 15.9216 21.3284 15.25 20.5 15.25C19.6716 15.25 19 15.9216 19 16.75C19 17.5784 19.6716 18.25 20.5 18.25C21.3284 18.25 22 17.5784 22 16.75ZM19.5 -1.96701e-07L19.5 13.3949C18.0543 13.8252 17 15.1645 17 16.75C17 18.3355 18.0543 19.6748 19.5 20.1051L19.5 24L21.5 24L21.5 20.1051C22.9457 19.6748 24 18.3355 24 16.75C24 15.1645 22.9457 13.8252 21.5 13.3949L21.5 -1.09278e-07L19.5 -1.96701e-07ZM2.5 13.3949L2.5 -9.39795e-07L4.5 -8.52372e-07L4.5 13.3949C5.94574 13.8252 7 15.1645 7 16.75C7 18.3355 5.94574 19.6748 4.5 20.1051L4.5 24L2.5 24L2.5 20.1051C1.05425 19.6748 -8.01471e-07 18.3355 -7.32166e-07 16.75C-6.62861e-07 15.1645 1.05425 13.8252 2.5 13.3949ZM3.5 15.25C4.32843 15.25 5 15.9216 5 16.75C5 17.5784 4.32843 18.25 3.5 18.25C2.67157 18.25 2 17.5784 2 16.75C2 15.9216 2.67157 15.25 3.5 15.25ZM13 24L13 11.8551C14.4457 11.4248 15.5 10.0855 15.5 8.5C15.5 6.91449 14.4457 5.57521 13 5.14494L13 -4.80825e-07L11 -5.68248e-07L11 5.14494C9.55426 5.57521 8.5 6.91449 8.5 8.5C8.5 10.0855 9.55425 11.4248 11 11.8551L11 24L13 24ZM13.5 8.5C13.5 7.67157 12.8284 7 12 7C11.1716 7 10.5 7.67157 10.5 8.5C10.5 9.32843 11.1716 10 12 10C12.8284 10 13.5 9.32843 13.5 8.5Z" fill="white" />
                                </svg>
                                Filters
                            </h3>
                        </div>
                        <div className="filter-body">
                            {isMobile && config.PropertySearchType != 'carboncredits' &&
                                <div className="search-form mob">
                                    {config.SearchTypes.length > 1 &&
                                        <div className="cell medium-3 xlarge-2 mb-2">
                                            <div className="rounded search-type">
                                                <Toggle name="SearchType" label1={config.SearchTypes[0].Value} value1={config.SearchTypes[0].Key} label2={config.SearchTypes[1].Value} value2={config.SearchTypes[1].Key} selected={config.SearchType} />
                                            </div>
                                        </div>
                                    }
                                    {config.SearchTypes.length == 1 &&
                                        <input type="hidden" name="SearchType" defaultValue={config.SearchTypes[0].Key} />
                                    }
                                    <div className="cell mb-2">
                                        <div className="rounded flex-container search-bar">
                                            <div className={searchType === 'location' ? 'search active w-100' : 'search w-100'}>
                                                <APIProvider apiKey={config.GmKey}>
                                                    <div className={'relative' + (show && ' is-active')}>
                                                        <GoogleMapsAutocomplete onPlaceSelect={setPlace} location={config.Location} hasFocus={e => { setSearchType(e); isMobile && toggleFilters(true); }} />
                                                    </div>
                                                </APIProvider>
                                            </div>
                                            <div className={searchType === 'location' ? 'indicator w-100' : 'indicator right w-100'}>
                                                <svg xmlns="http://www.w3.org/2000/svg" width="32" height="31" viewBox="0 0 32 31" fill="none">
                                                    <circle cx="16" cy="15.5" r="15.5" transform="rotate(-90 16 15.5)" fill="#16255B" />
                                                    <path className="a-right" d="M13.05 14.5701L8.5 18.8749L13.05 23.1797L13.05 19.7091L24.3543 19.7091L24.3543 18.0407L13.05 18.0407L13.05 14.5701Z" fill="#63BFB5" />
                                                    <path className="a-left" d="M19.95 15.6096L24.5 11.3048L19.95 7L19.95 10.4706L8.64565 10.4706L8.64565 12.139L19.95 12.139L19.95 15.6096Z" fill="white" />
                                                </svg>
                                            </div>
                                            <div className={searchType === 'property' ? 'search active relative w-100' : 'search relative w-100'}>
                                                <input name="PropertyName" id="Property" type="search" className="property-search rounded" placeholder="e.g. Property name" onFocus={e => setSearchType('property')} onChange={(e) => { e.stopPropagation() }} defaultValue={propertyName} />
                                                <svg className="key absolute" xmlns="http://www.w3.org/2000/svg" width="21" height="23" viewBox="0 0 25 23" fill="none">
                                                    <path fillRule="evenodd" clipRule="evenodd" d="M20.6425 3.55151L22.0571 2.13687L20.6429 0.722656L9.97656 11.389C8.99573 10.6905 7.79584 10.2797 6.5 10.2797C3.18629 10.2797 0.5 12.966 0.5 16.2797C0.5 19.5934 3.18629 22.2797 6.5 22.2797C9.81371 22.2797 12.5 19.5934 12.5 16.2797C12.5 14.9839 12.0892 13.784 11.3908 12.8032L14.9854 9.20856L17.1063 11.3295L19.2277 9.20814L17.1068 7.08723L18.5212 5.67283L22.0564 9.20809L24.1777 7.08677L20.6425 3.55151ZM2.5 16.2797C2.5 14.0706 4.29086 12.2797 6.5 12.2797C8.70914 12.2797 10.5 14.0706 10.5 16.2797C10.5 18.4889 8.70914 20.2797 6.5 20.2797C4.29086 20.2797 2.5 18.4889 2.5 16.2797Z" fill="black" fillOpacity="0.5" />
                                                </svg>
                                            </div>
                                        </div>
                                    </div>
                                    <div className="cell mb-2">
                                        <button type="submit" className="btn-search rounded" onClick={(e) => { e.preventDefault(); searchClick() }}>
                                            <svg className="icon">
                                                <use href="#search"></use>
                                            </svg>
                                            <span className="show-for-sr">Search by location or keyword</span>
                                        </button>
                                    </div>
                                </div>
                            }
                            <span id="allFilters"></span>
                            {config.ShowRadiusFilter && searchType === 'location' &&
                                <>
                                    <span id="radiusFilter"></span>
                                    <RadioButtonList items={areas} name="SearchRadius" selected={config.SearchRadius} label="Radius:" defaultOptionLabel="This area only" />
                                </>
                            }
                            {config.ShowPropertyTypeList &&
                                <>
                                    <span id="ptFilter"></span>
                                    <CheckboxList items={propertyTypes} name="PropertyType" label="Property types:" selected={config.PropertyType} onChange={() => setPtLabel(buildFilterLabel('PropertyType', 'All property types'))} />
                                </>
                            }
                            {config.PropertySearchType == 'bng' &&
                                <>
                                    <span id="ncaFilter"></span>
                                    <CheckboxList items={ncas} name="NationalCharacterArea" label="National Character Areas:" selected={config.NationalCharacterArea} onChange={()=> setNcaLabel(buildFilterLabel('NationalCharacterArea', 'All national character areas'))} />
                                    <span id="lpaFilter"></span>
                                    <CheckboxList items={lpas} name="LocalPlanningAuthority" label="Local Planning Authorities:" selected={config.LocalPlanningAuthority} onChange={() => setLpaLabel(buildFilterLabel('LocalPlanningAuthority', 'All local planning authorities'))} />
                                </>
                            }
                            {config.PropertySearchType == 'carboncredits' &&
                                <>
                                    <span id="unitTypesFilter"></span>
                                    <CheckboxList items={unitTypes} name="UnitType" label="Unit Types:" selected={config.UnitType} onChange={() => setUnitTypeLabel(buildFilterLabel('UnitType', 'All unit types'))} />
                                    <span id="projectTypesFilter"></span>
                                    <CheckboxList items={projectTypes} name="ProjectType" label="Project Types:" selected={config.UnitType} onChange={() => setProjectTypeLabel(buildFilterLabel('ProjectType', 'All project types'))} />
                                    <fieldset className="mt-3" id="vintageYearsFilter">
                                    <Dropdown items={vintageYears} label="Verified before:" name="VerifiedBeforeYear" defaultOptionLabel="Any" selected={config.VerifiedBeforeYear} triggerChange={updateYearsLabel} />
                                    </fieldset>
                                </>
                            }
                            {config.ShowSizeFilter &&
                                <fieldset className="mt-3" id="sizeFilter">
                                    <legend className="legend">Size ({config.SizeUnit})</legend>
                                    <Dropdown items={sizesFrom} label="Min size" name="MinSize" defaultOptionLabel="Any" selected={config.MinSize} triggerChange={updateSizeLabel} />
                                    <Dropdown items={sizesTo} label="Max size" name="MaxSize" defaultOptionLabel="Any" selected={config.MaxSize} triggerChange={updateSizeLabel} />
                                </fieldset>
                            }
                            {config.ShowPriceFilter && isSale &&
                                <fieldset className="mt-3" id="priceFilter">
                                    <legend className="legend">Price</legend>
                                    <Dropdown items={pricesFrom} label="Min price" name="MinPrice" defaultOptionLabel="Any" selected={config.MinPrice} triggerChange={updatePriceLabel} />
                                    <Dropdown items={pricesTo} label="Max price" name="MaxPrice" defaultOptionLabel="Any" selected={config.MaxPrice} triggerChange={updatePriceLabel} />
                                </fieldset>
                            }
                            {config.ShowPriceFilter && !isSale &&
                                <fieldset className="mt-3" id="rentalPriceFilter">
                                    <legend className="legend">Rent</legend>
                                    <Dropdown items={rentalPricesFrom} label="Min price" name="MinRentalPrice" defaultOptionLabel="Any" selected={config.MinRentalPrice} triggerChange={updateRentalPriceLabel} />
                                    <Dropdown items={rentalPricesTo} label="Max price" name="MaxRentalPrice" defaultOptionLabel="Any" selected={config.MaxRentalPrice} triggerChange={updateRentalPriceLabel} />
                                </fieldset>
                            }
                            {config.ShowSoldStcFilter &&
                                <fieldset className="mt-3">
                                    <legend className="legend">Show / hide</legend>
                                    <div>
                                        <div className="checked-item">
                                            <input type="checkbox" id="RemoveSoldStc_True" name="RemoveSoldStc" defaultChecked={config.RemoveSoldStc} value="True" />
                                            <label htmlFor="RemoveSoldStc_True" className="checked-item__label">Remove Let, Under Offer or Sold</label>
                                        </div>
                                    </div>
                                </fieldset>
                            }
                            {config.ShowTenureFilter && config.SearchType == 0 &&
                                <RadioButtonList items={config.TenureTypes.map(r => ({ name: r, value: r }))} name="Tenure" selected={config.Tenure ?? 'Any'} label="Tenure" />
                            }
                        </div>
                        <div className="filter-footer">
                            <a href="#" className="clear rounded mr-1" onClick={e => { e.preventDefault(); clearFilters(); toggleFilters(false); }}>Clear filters</a>
                            <a href="#" className="btn-search rounded ml-1" onClick={(e) => { e.preventDefault(); searchClick(); }}>
                                Apply filters
                            </a>
                        </div>
                    </div>
                </div>
            </form >
        </div >
    )
}